/**
 * File Editor Modal component for editing files using Monaco editor.
 * Provides syntax highlighting, save functionality, and unsaved changes warning.
 *
 * @module components/terminal/FileEditorModal
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import { Loader2, Save, AlertTriangle } from 'lucide-react';
import { Modal, Button, ConfirmModal } from '@/components/ui';
import { useDownloadFile, useWriteFile } from '@/hooks';
import { useDarkMode } from '@/hooks';
import type { editor } from 'monaco-editor';

interface FileEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  sandboxId: string;
  /** Optional sandbox group ID — routes file operations through group-scoped URLs */
  sandboxGroupId?: string;
  /** Optional container name for pod sandboxes */
  containerName?: string;
  filePath: string;
  fileName: string;
  onSaveSuccess?: () => void;
}

/**
 * Maximum file size allowed for editing (1MB)
 */
export const MAX_EDIT_FILE_SIZE = 1 * 1024 * 1024;

/**
 * Get Monaco language from file extension
 */
function getLanguageFromFileName(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';

  const languageMap: Record<string, string> = {
    // JavaScript/TypeScript
    js: 'javascript',
    jsx: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    mjs: 'javascript',
    cjs: 'javascript',

    // Web
    html: 'html',
    htm: 'html',
    css: 'css',
    scss: 'scss',
    less: 'less',
    json: 'json',
    xml: 'xml',
    svg: 'xml',

    // Programming languages
    py: 'python',
    rb: 'ruby',
    java: 'java',
    c: 'c',
    cpp: 'cpp',
    cc: 'cpp',
    cxx: 'cpp',
    h: 'c',
    hpp: 'cpp',
    cs: 'csharp',
    go: 'go',
    rs: 'rust',
    php: 'php',
    swift: 'swift',
    kt: 'kotlin',
    scala: 'scala',
    r: 'r',
    lua: 'lua',
    pl: 'perl',

    // Shell/Config
    sh: 'shell',
    bash: 'shell',
    zsh: 'shell',
    fish: 'shell',
    ps1: 'powershell',
    bat: 'bat',
    cmd: 'bat',

    // Config files
    yaml: 'yaml',
    yml: 'yaml',
    toml: 'ini',
    ini: 'ini',
    conf: 'ini',
    cfg: 'ini',
    env: 'ini',

    // Documentation
    md: 'markdown',
    markdown: 'markdown',
    rst: 'plaintext',
    txt: 'plaintext',

    // Data formats
    sql: 'sql',
    graphql: 'graphql',
    gql: 'graphql',

    // Docker/DevOps
    dockerfile: 'dockerfile',

    // Other
    makefile: 'makefile',
    cmake: 'plaintext',
  };

  // Handle special filenames
  const fileNameLower = fileName.toLowerCase();
  if (fileNameLower === 'dockerfile') return 'dockerfile';
  if (fileNameLower === 'makefile' || fileNameLower === 'gnumakefile') return 'makefile';
  if (fileNameLower.startsWith('.env')) return 'ini';
  if (fileNameLower === '.gitignore' || fileNameLower === '.dockerignore') return 'plaintext';
  if (
    fileNameLower === '.bashrc' ||
    fileNameLower === '.zshrc' ||
    fileNameLower === '.bash_profile'
  )
    return 'shell';

  return languageMap[ext] || 'plaintext';
}

/**
 * File Editor Modal component
 */
export function FileEditorModal({
  isOpen,
  onClose,
  sandboxId,
  sandboxGroupId,
  containerName,
  filePath,
  fileName,
  onSaveSuccess,
}: FileEditorModalProps) {
  const [content, setContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const [isDark] = useDarkMode();
  const downloadMutation = useDownloadFile();
  const writeMutation = useWriteFile();

  const hasUnsavedChanges = content !== originalContent;

  // Load file content when modal opens
  useEffect(() => {
    if (isOpen && filePath) {
      setIsLoading(true);
      setLoadError(null);

      downloadMutation
        .mutateAsync({ sandboxId, path: filePath, containerName, sandboxGroupId })
        .then(async (blob) => {
          const text = await blob.text();
          setContent(text);
          setOriginalContent(text);
          setIsLoading(false);
        })
        .catch((error) => {
          setLoadError(error.message || 'Failed to load file');
          setIsLoading(false);
        });
    }
  }, [isOpen, filePath, sandboxId, sandboxGroupId, containerName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle editor mount
  const handleEditorMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    // Force LF line endings since sandboxes always run Linux
    editor.getModel()?.pushEOL(monaco.editor.EndOfLineSequence.LF);
    editor.focus();
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      await writeMutation.mutateAsync({
        sandboxId,
        path: filePath,
        content,
        createDirs: false,
        containerName,
        sandboxGroupId,
      });
      setOriginalContent(content);
      onSaveSuccess?.();
    } catch {
      // Error is handled by the mutation's onError
    }
  }, [sandboxId, sandboxGroupId, filePath, content, containerName, writeMutation, onSaveSuccess]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  // Handle confirm discard
  const handleConfirmDiscard = useCallback(() => {
    setShowUnsavedWarning(false);
    setContent('');
    setOriginalContent('');
    onClose();
  }, [onClose]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!writeMutation.isPending && hasUnsavedChanges) {
          handleSave();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleSave, writeMutation.isPending, hasUnsavedChanges]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setContent('');
      setOriginalContent('');
      setLoadError(null);
      setIsLoading(true);
    }
  }, [isOpen]);

  const language = getLanguageFromFileName(fileName);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={`Edit: ${fileName}`}
        description={filePath}
        size="5xl"
        footer={
          <>
            <Button variant="secondary" onClick={handleClose} disabled={writeMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              isLoading={writeMutation.isPending}
              disabled={!hasUnsavedChanges || isLoading || !!loadError}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="h-[60vh] min-h-[400px] -mx-6 -my-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading file...</span>
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <AlertTriangle className="h-8 w-8 mb-2" />
              <p className="text-sm font-medium">Failed to load file</p>
              <p className="text-xs text-gray-500 mt-1">{loadError}</p>
            </div>
          ) : (
            <Editor
              height="100%"
              language={language}
              value={content}
              onChange={(value) => setContent(value || '')}
              onMount={handleEditorMount}
              theme={isDark ? 'vs-dark' : 'light'}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                wordWrap: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
                insertSpaces: true,
                renderWhitespace: 'selection',
                bracketPairColorization: { enabled: true },
              }}
              loading={
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              }
            />
          )}
        </div>

        {/* Unsaved changes indicator */}
        {hasUnsavedChanges && !isLoading && !loadError && (
          <div className="absolute top-4 right-12 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            Unsaved changes
          </div>
        )}
      </Modal>

      {/* Unsaved changes warning modal */}
      <ConfirmModal
        isOpen={showUnsavedWarning}
        onClose={() => setShowUnsavedWarning(false)}
        onConfirm={handleConfirmDiscard}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard"
        cancelText="Keep Editing"
        variant="danger"
      />
    </>
  );
}
